/**
 * Avatar Option Descriptor
 *
 * Turns a DiceBear 10 style into the flat, grid-friendly option map the editor
 * renders. Replaces the hand-rolled JSON Schema walking this file used to do:
 * v10 exposes `OptionsDescriptor`, which already reports each option's type and
 * candidate values, and `Style#colors()` for per-style palettes.
 */

import {
  OptionsDescriptor,
  type Style,
  type StyleDefinition,
} from '@dicebear/core';

/** Not exported from the package root. */
type StyleDefinitionElement = StyleDefinition['canvas']['elements'][number];

export interface StyleOption {
  values: string[];
  isColor: boolean;
  isArray: boolean;
  /** The component is optional, so `''` (leave it out) is a valid choice. */
  hasProbability: boolean;
  /** The style's own default probability (0–100) that the component shows. */
  probability: number;
}

export type StyleOptions = Record<string, StyleOption>;

/**
 * Canvas-level options. They are exposed through dedicated controls (or not at
 * all), so they never appear as a grid of avatar thumbnails.
 */
const NON_GRID_OPTIONS = new Set([
  'seed',
  'size',
  'title',
  'idRandomization',
  'fontFamily',
  'fontWeight',
  'flip',
  'scale',
  'borderRadius',
  'rotate',
  'translateX',
  'translateY',
]);

/**
 * v10 gradient controls. Kept out of the grid for now — a gradient is not
 * expressible as a single selectable value.
 */
const GRADIENT_OPTION_PATTERN = /Color(Fill|FillStops|Angle)$/;

/** DiceBear 10 rejects the literal `transparent`; 8-digit hex is the escape. */
export const TRANSPARENT_COLOR = '00000000';

const DEFAULT_BACKGROUND_COLORS = [
  'fff1f5',
  'ffdde6',
  'ffc8d8',
  'ffb3c9',
  'fc9ebb',
  'fff2ef',
  'ffdfd9',
  'ffcbc2',
  'ffb7ab',
  'ffa293',
  'fff3e9',
  'ffe1c9',
  'ffcfa8',
  'ffbc84',
  'f5ac6f',
  'fff5d9',
  'ffe5a0',
  'f7d67c',
  'e9c96e',
  'dbbb60',
  'edffb9',
  'ddf399',
  'cfe58c',
  'c2d77e',
  'b5ca71',
  'dfffe4',
  'aefebe',
  'a0efb1',
  '85d496',
  'd8fff6',
  '91ffea',
  '77f3dc',
  '68e5cf',
  '59d7c1',
  'e2faff',
  'b6f4ff',
  '80ecff',
  '5fe0f6',
  '4fd3e8',
  'ecf7ff',
  'd1ebff',
  'b5e0ff',
  '97d4ff',
  '77c8ff',
  'f1f5ff',
  'dee7ff',
  'cbd9ff',
  'b8caff',
  'a5bcff',
  'f7f3ff',
  'ebe2ff',
  'e0d0ff',
  'd5bfff',
  'caadfe',
  'fff0fd',
  'ffdafa',
  'ffc2f9',
  'f7b0f0',
  'ffffff',
];

/** The editor stores and displays colors without the leading `#`. */
const stripHash = (color: string): string => color.replace(/^#/, '');

/**
 * True when the style paints its own opaque, canvas-sized fill over the
 * background. `glyphs` does, so its `backgroundColor` is applied and then
 * covered — the markup differs per colour while the picture never does, which
 * is invisible to the rendered-output comparison in `avatarCombinations`.
 */
function hidesBackground(style: Style): boolean {
  const canvas = style.definition().canvas;

  const covers = (elements: readonly StyleDefinitionElement[]): boolean =>
    elements.some((element) => {
      // Masks and definitions describe paint, they do not apply it.
      if (
        element.name === 'mask' ||
        element.name === 'defs' ||
        element.name === 'clipPath'
      ) {
        return false;
      }

      const attributes = element.attributes ?? {};
      if ('opacity' in attributes || 'fill-opacity' in attributes) return false;

      const fill = attributes.fill;
      // DiceBear ≥ 10.7 exports the canvas rect as an equivalent path.
      const canvasShape =
        (element.name === 'rect' &&
          Number(attributes.width) === canvas.width &&
          Number(attributes.height) === canvas.height) ||
        (element.name === 'path' &&
          attributes.d === `M0 0h${canvas.width}v${canvas.height}H0z`);
      const fillsCanvas =
        canvasShape && typeof fill === 'string' && fill !== 'none';

      return fillsCanvas || covers(element.children ?? []);
    });

  return covers(canvas.elements);
}

/**
 * `fooVariant` is gated by `fooProbability` in v10 (v9 paired `foo` with
 * `fooProbability`).
 */
const probabilityKeyFor = (optionKey: string): string =>
  `${componentNameFor(optionKey)}Probability`;

/** `eyesVariant` is driven by the `eyes` component. */
const componentNameFor = (optionKey: string): string =>
  optionKey.replace(/Variant$/, '');

/** `accessoriesColor` is driven by the `accessories` entry in `colors`. */
const colorNameFor = (optionKey: string): string =>
  optionKey.replace(/Color$/, '');

/**
 * Builds the selectable options for a style, keyed by DiceBear option name.
 */
export function describeStyleOptions(style: Style): StyleOptions {
  const descriptor = new OptionsDescriptor(style).toJSON();
  const colors = style.colors();
  const components = style.components();
  const backgroundIsHidden = hidesBackground(style);
  const result: StyleOptions = {};

  for (const [key, field] of Object.entries(descriptor)) {
    if (NON_GRID_OPTIONS.has(key)) continue;
    if (key.endsWith('Probability')) continue;
    if (GRADIENT_OPTION_PATTERN.test(key)) continue;
    if (key === 'backgroundColor' && backgroundIsHidden) continue;

    // v10 exposes a `*Probability` option for *every* component, but only the
    // ones the style gives a default below 100 are genuinely optional (glasses,
    // facial hair). Treating a mandatory component as optional lets it be
    // switched off, which renders an empty avatar.
    const probability =
      probabilityKeyFor(key) in descriptor
        ? (components.get(componentNameFor(key))?.probability() ?? 100)
        : 100;
    const hasProbability = probability < 100;
    const isArray = 'list' in field && field.list === true;
    const values: string[] = [];

    // An empty value means "leave this component out", which v10 expresses as
    // a zero probability.
    if (hasProbability) {
      values.push('');
    }

    if (field.type === 'enum') {
      values.push(...field.values);
    } else if (field.type === 'color') {
      if (key === 'backgroundColor') {
        values.push(TRANSPARENT_COLOR, ...DEFAULT_BACKGROUND_COLORS);
      } else {
        const palette = colors.get(colorNameFor(key))?.values() ?? [];
        values.push(...palette.map(stripHash));
      }
    } else {
      // Numbers, ranges, booleans and free-text have no thumbnail grid.
      continue;
    }

    // A single choice is not worth a tab, unless it is a color the user can
    // override with the picker.
    const isColor = field.type === 'color';
    if (values.length <= 1 && !isColor) continue;

    result[key] = {
      values: Array.from(new Set(values)),
      isColor,
      isArray,
      hasProbability,
      probability,
    };
  }

  return result;
}

/**
 * Maps the editor's flat `option -> value` map onto DiceBear options: list
 * fields are wrapped in an array, and an empty value switches the component off
 * through its probability.
 */
export function toDicebearOptions(
  options: Record<string, string>,
  styleOptions: StyleOptions,
): Record<string, unknown> {
  const converted: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(options)) {
    const styleOption = styleOptions[key];

    // Options the current style does not accept would fail v10 validation.
    if (!styleOption) continue;

    if (styleOption.hasProbability) {
      converted[probabilityKeyFor(key)] = value ? 100 : 0;
    }

    if (!value) {
      continue;
    }

    converted[key] = styleOption.isArray ? [value] : value;
  }

  return converted;
}

/**
 * Get random value from option values
 */
export function getRandomOptionValue(values: string[]): string {
  const index = Math.floor(Math.random() * values.length);
  return values[index] || '';
}

/**
 * Generate random options for a style.
 *
 * Optional components are left out at the rate the style itself defines, so
 * rare extras (glasses, facial hair) stay rare and mandatory ones always
 * render — picking `''` uniformly would blank out whole avatars.
 */
export function generateRandomOptions(
  styleOptions: StyleOptions,
): Record<string, string> {
  const result: Record<string, string> = {};

  for (const key in styleOptions) {
    const option = styleOptions[key];
    if (!option) continue;

    if (option.hasProbability && Math.random() * 100 >= option.probability) {
      result[key] = '';
      continue;
    }

    result[key] = getRandomOptionValue(
      option.values.filter((value) => value !== ''),
    );
  }

  return result;
}
